import React, { useCallback, useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import appConfig from 'app-config';
import PropTypes from 'prop-types';

import { TranslatorContext } from '@jutro/locale';

import { wizardProps } from 'wmic-pe-portals-custom-wizard-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { Accordion } from '@jutro/legacy/components';

import { WMICScrollToError } from 'wmic-pe-components-platform-react';
import { useWizardModals, useDocumentTitle, WMICWizardSubmissionPage } from 'wmic-pe-portals-wizard-components-ui';
import { WMICCPLocationCoverages } from 'wmic-pe-capability-gateway-quoteandbind-cp-react';
import { WMICGLPropertyLineCoverages } from 'wmic-pe-capability-gateway-quoteandbind-gl-react';
import { FlowStepId, WMICValidationUtil, WMICVariousUtil } from 'wmic-pe-portals-utils-js';

import WMICCPCoverageUtil from 'wmic-pe-capability-gateway-quoteandbind-cp-react/util/WMICCPCoverageUtil';

import { messages as commonMessages } from 'wmic-pe-capability-gateway-quoteandbind-common-react';
import metadata from './WMICPropertyPage.metadata.json5';
import messages from './WMICPropertyPage.messages';
import styles from './WMICPropertyPage.module.scss';

const LOCATION_PATH = 'lobData.commercialProperty.coverables.locations';

const structureCustomQuote = (submissionVM, affectedQuote, clauses) =>
    // convert OfferingDTO to CustomQuotedDTO structure
    ({
        quote: affectedQuote,
        quoteID: submissionVM.quoteID.value,
        sessionUUID: submissionVM.sessionUUID.value,
        periodStart: submissionVM.baseData.periodStartDate.value,
        periodEnd: submissionVM.baseData.periodEndDate.value,
        coverages: clauses,
    });

const getCustomQuote = (vm, lobPath, lobName, filterChangedClauses = false) => {
    const lobOffering = _.get(vm, `${lobPath}.value`);

    let clausesToUpdate = {
        [lobName]: lobOffering.coverages,
    };

    if (filterChangedClauses) {
        clausesToUpdate = ClausesUtil.structureClausesForServer(lobOffering.coverages, lobName, null);
    }

    return structureCustomQuote(vm, lobOffering, clausesToUpdate);
};

function WMICPropertyPage(props) {
    const { wizardData: submissionVM, updateWizardData, underwritingIssues } = props;
    const translator = useContext(TranslatorContext);
    const [showErrors, setShowErrors] = useState(false);
    const [scrollToError, setScrollToError] = useState(false); // we toggle this when we want to scroll to the first error on the page

    const { CustomQuoteService } = useDependencies('CustomQuoteService');
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { setWizardLoading } = useWizardModals();
    const { authHeader } = useAuthentication();
    const {
        onValidate,
        disregardFieldValidation,
        initialValidation,
        registerInitialComponentValidation,
        isComponentValid,
    } = useValidation('cpPropertyPage');
    const viewModelService = useContext(ViewModelServiceContext);

    useDocumentTitle(translator(messages.title), submissionVM);

    const validateTableForm = useCallback(
        () =>
            WMICValidationUtil.validatePageEntities(submissionVM, [`${LOCATION_PATH}`]) &&
            !WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_PROPERTY),
        [submissionVM]
    );

    useEffect(() => {
        registerInitialComponentValidation(validateTableForm);
    }, [registerInitialComponentValidation, validateTableForm]);

    useEffect(() => {
        if (isComponentValid) {
            setShowErrors(false);
        }
    }, [isComponentValid]);

    useEffect(() => {
        submissionVM.value.flowStepId_WMIC = FlowStepId.CP_PROPERTY;
    }, [submissionVM.value]);

    const getRemovedFieldsForLob = useCallback(
        (lobName, lobPath, newSubmissionVM) => {
            let allRemovedFields = [];

            switch (lobName) {
                case 'commercialProperty':
                    const removedFieldsFromLocationCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.locationCoverages`
                    );

                    const removedFieldsFromBuildingCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.buildingCoverages`
                    );

                    allRemovedFields = [...removedFieldsFromLocationCoverages, ...removedFieldsFromBuildingCoverages];

                    break;
                case 'generalLiability':
                    const removedFieldsFromPropertyLineCoverages = ClausesUtil.getRemovedClausesID(
                        submissionVM,
                        newSubmissionVM,
                        `${lobPath}.coverages.propertyLineCoverages`
                    );

                    allRemovedFields = [...removedFieldsFromPropertyLineCoverages];

                    break;
                default:
                    break;
            }

            return allRemovedFields;
        },
        [submissionVM]
    );

    const onClauseChange = useCallback(
        _basePath => {
            const lobPath = 'lobData.commercialProperty.offerings.children[0]';
            const lobName = ClausesUtil.getLobNameFromPath(lobPath);
            const customQuote = getCustomQuote(submissionVM, lobPath, lobName, true);
            setWizardLoading(true);

            return CustomQuoteService.updateCustomQuoteCoveragesAndQuoteData(
                customQuote,
                submissionVM.value,
                authHeader
            )
                .then(response => {
                    const newSubmissionVM = viewModelService.clone(submissionVM);
                    newSubmissionVM.value = response;

                    const allRemovedFields = getRemovedFieldsForLob(lobName, lobPath, newSubmissionVM);

                    disregardFieldValidation(allRemovedFields);
                    updateWizardData(newSubmissionVM);
                })
                .finally(() => {
                    setWizardLoading(false);
                });
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            submissionVM,
            setWizardLoading,
            CustomQuoteService,
            authHeader,
            viewModelService,
            getRemovedFieldsForLob,
            disregardFieldValidation,
        ]
    );

    const syncCoverages = useCallback(
        (value, changedPath) => {
            const basePath = ClausesUtil.getObjectPathFromChangedPath(changedPath);
            return onClauseChange(basePath);
        },
        [onClauseChange]
    );

    const changeSubmission = useCallback(
        (value, changedPath) => {
            updateWizardData(ClausesUtil.setClauseValue(submissionVM, value, changedPath));
        },
        // Cannot include updateWizardData in the dependency array since calling it causes a new function to be created, which would cause in infinite loop in the useEffect below
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [submissionVM]
    );

    const changeSubmissionAndSync = useCallback(
        (value, changedPath) => {
            changeSubmission(value, changedPath);
            return syncCoverages(value, changedPath);
        },
        [changeSubmission, syncCoverages]
    );

    const commonOverrides = {
        quoteID: submissionVM.quoteID.value,
        changeSubmissionAndSync,
        changeSubmission,
        onClauseChange,
        syncCoverages,
        underwritingIssues,
        onValidate,
    };

    const _iterableProps = () => {
        const locationOverrides = _.get(submissionVM, `${LOCATION_PATH}.children`).map((location, index) => {
            const locationCoverages = WMICCPCoverageUtil.getCoveragesForLocation(
                _.get(submissionVM, 'lobData.commercialProperty'),
                location.publicID.value
            );

            const locationOverride = {
                [`locationCoveragesIterableComponent${index}`]: {
                    locationVM: location,
                    locationIndex: index,
                    baseData: submissionVM.baseData,
                    locationCoverages,
                    jobVM: submissionVM,
                    ...commonOverrides,
                },
            };

            return { ...locationOverride };
        });

        return Object.assign({}, ...locationOverrides);
    };

    const overrideProps = {
        locationCoveragesIterableAccordionContainer: {
            data: _.get(submissionVM, `${LOCATION_PATH}.children`, []),
        },
        propertyLineCoverage: {
            ...commonOverrides,
            jobVM: submissionVM,
        },
        ..._iterableProps(),
    };

    const onNext = useCallback(async () => {
        try {
            if (!isComponentValid) {
                setShowErrors(true);
                setScrollToError(Date.now());
                return false;
            }

            setWizardLoading(true);
            const newSubmissionVM = viewModelService.clone(submissionVM);

            _.unset(newSubmissionVM.value, 'bindData');

            submissionVM.value = await LoadSaveService.updateDraftSubmissionWithLOBData(
                newSubmissionVM.value,
                authHeader
            );

            if (WMICValidationUtil.hasDtoValidationErrors(submissionVM, FlowStepId.CP_PROPERTY)) {
                WMICVariousUtil.scrollToTop();
                return false;
            }

            return submissionVM;
        } finally {
            setWizardLoading(false);
        }
    }, [LoadSaveService, authHeader, isComponentValid, setWizardLoading, submissionVM, viewModelService]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onChangeSubmissionAndSync: changeSubmissionAndSync,
            onChangeSubmission: changeSubmission,
            onSyncCoverages: syncCoverages,
        },
        resolveComponentMap: {
            WMICCPLocationCoverages,
            WMICGLPropertyLineCoverages,
            Accordion,
        },
    };

    return (
        <WMICWizardSubmissionPage
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            cancelLabel={commonMessages.saveAndExit}
            onNext={onNext}
            flowStepId={FlowStepId.CP_PROPERTY}
        >
            <WMICScrollToError counter={scrollToError} />
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                classNameMap={resolvers.resolveClassNameMap}
                callbackMap={resolvers.resolveCallbackMap}
                componentMap={resolvers.resolveComponentMap}
                onValidationChange={onValidate}
            />
        </WMICWizardSubmissionPage>
    );
}

WMICPropertyPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func,
    }).isRequired,
    ...wizardProps,
};

export default WMICPropertyPage;
